import React, {useEffect, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {ArrowDown, ArrowUp, Check, Loader, Pencil, Plus, Trash2, X} from "lucide-react";
import Button from "../Common/Button";
import toast from "react-hot-toast";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import IOSSwitch from "../Common/IOSSwitch";
import {ListLoader} from "../../stock/StockUI";
import {Field, Section} from "../../ClientsTable/clientFormUI";
import {
    REFUND_MODES,
    apiErrorMessage,
    createReturnReason,
    deleteReturnReason,
    fetchReturnReasons,
    fetchReturnSettings,
    inputClass,
    updateReturnReason,
    updateReturnSettings,
} from "./returnsApi";

// The page header's « Enregistrer » submits this form
export const RULES_FORM_ID = "returns-rules-form";

const SectionTitle = ({title, description}) => (
    <div>
        <h2 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100">{title}</h2>
        {description && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</p>}
    </div>
);

// Grey switch row, like the Actif / Stockable toggles of the product form
const SwitchRow = ({label, description, children}) => (
    <div className="flex items-center justify-between gap-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <span>
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
            {description && <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</span>}
        </span>
        {children}
    </div>
);

// ─── Règles ──────────────────────────────────────────────────────────────────

const RulesSection = ({canManage}) => {
    const queryClient = useQueryClient();
    const [form, setForm] = useState(null);

    const {data: settings, isLoading, isError} = useQuery({
        queryKey: ["returns-settings"],
        queryFn: fetchReturnSettings,
    });

    useEffect(() => {
        if (settings) {
            setForm({
                enabled: settings.enabled,
                refund_modes: settings.refund_modes,
                max_days: settings.max_days ?? "",
                credit_note_validity_days: settings.credit_note_validity_days ?? "",
                require_reason: settings.require_reason,
            });
        }
    }, [settings]);

    const saveMutation = useMutation({
        mutationFn: updateReturnSettings,
        onSuccess: (saved) => {
            queryClient.setQueryData(["returns-settings"], saved);
            toast.success("Réglages des retours enregistrés");
        },
        onError: (error) => toast.error(apiErrorMessage(error, "Impossible d'enregistrer les réglages")),
    });

    if (isLoading || (!form && !isError)) {
        return <ListLoader label="Chargement des réglages..."/>;
    }
    if (isError) {
        return <p className="text-sm text-red-500 p-6 text-center">Impossible de charger les réglages des retours.</p>;
    }

    const toggleMode = (key) =>
        setForm((prev) => ({
            ...prev,
            refund_modes: prev.refund_modes.includes(key)
                ? prev.refund_modes.filter((m) => m !== key)
                : [...prev.refund_modes, key],
        }));

    const handleSave = (e) => {
        e.preventDefault();
        if (form.enabled && form.refund_modes.length === 0) {
            toast.error("Activez au moins un mode de remboursement");
            return;
        }
        saveMutation.mutate({
            enabled: form.enabled,
            refund_modes: form.refund_modes,
            max_days: form.max_days === "" ? null : Number(form.max_days),
            credit_note_validity_days: form.credit_note_validity_days === "" ? null : Number(form.credit_note_validity_days),
            require_reason: form.require_reason,
        });
    };

    return (
        <form id={RULES_FORM_ID} onSubmit={handleSave} className="space-y-6 sm:space-y-8">
            <div className="space-y-4">
                <SectionTitle
                    title="Retours & échanges en caisse"
                    description="Active le mode Retour / Échange sur toutes les caisses du commerce, à leur prochaine synchronisation. Les employés ont aussi besoin de la permission « Retours et échanges » dans leur profil."
                />
                <SwitchRow label="Activé :">
                    <IOSSwitch
                        checked={form.enabled}
                        disabled={!canManage}
                        onChange={(checked) => setForm((prev) => ({...prev, enabled: checked}))}
                    />
                </SwitchRow>
            </div>

            <Section>
                <SectionTitle title="Modes de remboursement" description="Le caissier choisit parmi les modes cochés à chaque retour."/>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {REFUND_MODES.map(({key, label, description}) => {
                        const selected = form.refund_modes.includes(key);
                        return (
                            <button
                                key={key}
                                type="button"
                                disabled={!canManage}
                                onClick={() => toggleMode(key)}
                                className={`flex items-start gap-3 text-left p-4 rounded-lg border transition disabled:cursor-not-allowed ${
                                    selected
                                        ? "border-primary bg-primary/5"
                                        : "border-gray-200 dark:border-gray-600 hover:border-primary"
                                }`}
                            >
                                <span className={`mt-0.5 w-5 h-5 shrink-0 rounded-md flex items-center justify-center ${
                                    selected ? "bg-primary" : "border border-gray-300 dark:border-gray-500"
                                }`}>
                                    {selected && <Check className="w-3.5 h-3.5 text-white"/>}
                                </span>
                                <span>
                                    <span className="block font-medium text-gray-800 dark:text-white">{label}</span>
                                    <span className="block text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</span>
                                </span>
                            </button>
                        );
                    })}
                </div>
            </Section>

            <Section>
                <SectionTitle title="Règles"/>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <Field id="max_days" label="Délai de retour (jours)">
                        <input id="max_days" type="number" min="1" step="1" disabled={!canManage} placeholder="Illimité"
                               value={form.max_days}
                               onChange={(e) => setForm((prev) => ({...prev, max_days: e.target.value}))}
                               className={inputClass}/>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Au-delà, il faut un employé avec la permission « Retour hors délai ». Vide : pas de limite.
                        </p>
                    </Field>
                    <Field id="credit_note_validity_days" label="Validité des avoirs (jours)">
                        <input id="credit_note_validity_days" type="number" min="1" step="1" disabled={!canManage} placeholder="Illimitée"
                               value={form.credit_note_validity_days}
                               onChange={(e) => setForm((prev) => ({...prev, credit_note_validity_days: e.target.value}))}
                               className={inputClass}/>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Vide : les avoirs n'expirent jamais.</p>
                    </Field>
                </div>
                <SwitchRow label="Motif obligatoire :" description="Le caissier doit choisir un motif pour chaque article retourné.">
                    <IOSSwitch
                        checked={form.require_reason}
                        disabled={!canManage}
                        onChange={(checked) => setForm((prev) => ({...prev, require_reason: checked}))}
                    />
                </SwitchRow>
            </Section>

            {saveMutation.isPending && (
                <p className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <Loader className="w-4 h-4 animate-spin text-primary"/> Enregistrement…
                </p>
            )}
        </form>
    );
};

// ─── Motifs ──────────────────────────────────────────────────────────────────

const ReasonRow = ({reason, index, count, canManage, onMove, onSave, onDelete, saving}) => {
    const [editing, setEditing] = useState(false);
    const [label, setLabel] = useState(reason.label);

    useEffect(() => setLabel(reason.label), [reason.label]);

    const submit = () => {
        if (!label.trim()) return;
        onSave({id: reason.id, label: label.trim()});
        setEditing(false);
    };

    return (
        <li className="flex flex-wrap items-center gap-3 px-3 sm:px-4 py-3">
            <div className="flex-1 min-w-[10rem]">
                {editing ? (
                    <div className="flex items-center gap-2">
                        <input
                            autoFocus
                            value={label}
                            maxLength={100}
                            onChange={(e) => setLabel(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") submit();
                                if (e.key === "Escape") setEditing(false);
                            }}
                            className={`${inputClass} py-1.5`}
                        />
                        <button type="button" onClick={submit} className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg" title="Enregistrer">
                            <Check className="w-4 h-4"/>
                        </button>
                        <button type="button" onClick={() => setEditing(false)} className="p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" title="Annuler">
                            <X className="w-4 h-4"/>
                        </button>
                    </div>
                ) : (
                    <span className={`text-sm font-medium ${reason.active ? "text-gray-900 dark:text-gray-100" : "text-gray-400 line-through"}`}>
                        {reason.label}
                    </span>
                )}
            </div>

            <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                <IOSSwitch
                    checked={reason.restock_default}
                    disabled={!canManage || saving}
                    onChange={(checked) => onSave({id: reason.id, restock_default: checked})}
                />
                Remis en stock
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                <IOSSwitch
                    checked={reason.active}
                    disabled={!canManage || saving}
                    onChange={(checked) => onSave({id: reason.id, active: checked})}
                />
                Actif
            </label>

            {canManage && (
                <div className="flex items-center gap-1">
                    <button type="button" disabled={index === 0} onClick={() => onMove(index, -1)}
                            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-30" title="Monter">
                        <ArrowUp className="w-4 h-4"/>
                    </button>
                    <button type="button" disabled={index === count - 1} onClick={() => onMove(index, 1)}
                            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-30" title="Descendre">
                        <ArrowDown className="w-4 h-4"/>
                    </button>
                    <button type="button" onClick={() => setEditing(true)}
                            className="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/10 rounded-lg" title="Renommer">
                        <Pencil className="w-4 h-4"/>
                    </button>
                    <button type="button" onClick={() => onDelete(reason)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg" title="Supprimer">
                        <Trash2 className="w-4 h-4"/>
                    </button>
                </div>
            )}
        </li>
    );
};

const ReasonsSection = ({canManage}) => {
    const queryClient = useQueryClient();
    const [newLabel, setNewLabel] = useState("");
    const [newRestock, setNewRestock] = useState(true);
    const [toDelete, setToDelete] = useState(null);

    const {data: reasons = [], isLoading, isError} = useQuery({
        queryKey: ["returns-reasons"],
        queryFn: fetchReturnReasons,
    });

    const refresh = () => queryClient.invalidateQueries({queryKey: ["returns-reasons"]});
    const onError = (fallback) => (error) => toast.error(apiErrorMessage(error, fallback));

    const createMutation = useMutation({
        mutationFn: createReturnReason,
        onSuccess: () => {
            setNewLabel("");
            setNewRestock(true);
            refresh();
        },
        onError: onError("Impossible d'ajouter le motif"),
    });
    const updateMutation = useMutation({
        mutationFn: updateReturnReason,
        onSuccess: refresh,
        onError: onError("Impossible de modifier le motif"),
    });
    const deleteMutation = useMutation({
        mutationFn: deleteReturnReason,
        onSuccess: () => {
            toast.success("Motif supprimé");
            refresh();
        },
        onError: onError("Impossible de supprimer le motif"),
    });

    // Swaps positions with the neighbour, then renumbers every reason so
    // positions stay contiguous even after deletions.
    const move = async (index, delta) => {
        const ordered = [...reasons];
        const [moved] = ordered.splice(index, 1);
        ordered.splice(index + delta, 0, moved);
        try {
            await Promise.all(
                ordered
                    .map((reason, position) => ({reason, position}))
                    .filter(({reason, position}) => reason.position !== position)
                    .map(({reason, position}) => updateReturnReason({id: reason.id, position}))
            );
        } catch (error) {
            toast.error(apiErrorMessage(error, "Impossible de réordonner les motifs"));
        }
        refresh();
    };

    const handleAdd = (e) => {
        e.preventDefault();
        if (!newLabel.trim()) return;
        createMutation.mutate({label: newLabel.trim(), restock_default: newRestock});
    };

    return (
        <Section>
            <SectionTitle
                title="Motifs de retour"
                description="Proposés au caissier pour chaque article retourné. « Remis en stock » pré-coche la remise en stock (décochez-le pour un motif comme « Défectueux »)."
            />

            {isLoading ? (
                <ListLoader label="Chargement des motifs..."/>
            ) : isError ? (
                <p className="text-sm text-red-500 text-center py-6">Impossible de charger les motifs.</p>
            ) : (
                <ul className="divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg">
                    {reasons.length === 0 && (
                        <li className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400 text-center">Aucun motif pour le moment.</li>
                    )}
                    {reasons.map((reason, index) => (
                        <ReasonRow
                            key={reason.id}
                            reason={reason}
                            index={index}
                            count={reasons.length}
                            canManage={canManage}
                            saving={updateMutation.isPending}
                            onMove={move}
                            onSave={(payload) => updateMutation.mutate(payload)}
                            onDelete={setToDelete}
                        />
                    ))}
                </ul>
            )}

            {canManage && (
                <form onSubmit={handleAdd} className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <input
                        value={newLabel}
                        maxLength={100}
                        onChange={(e) => setNewLabel(e.target.value)}
                        placeholder="Nouveau motif (ex. Taille, Défectueux, Changé d'avis)"
                        className={`${inputClass} flex-1`}
                    />
                    <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        <IOSSwitch checked={newRestock} onChange={setNewRestock}/>
                        Remis en stock
                    </label>
                    <Button type="submit" variant="tinted" icon={Plus} loading={createMutation.isPending}
                            disabled={!newLabel.trim()}>
                        Ajouter
                    </Button>
                </form>
            )}

            {toDelete && (
                <ConfirmationDialog
                    title="Supprimer ce motif ?"
                    message={`« ${toDelete.label} » ne sera plus proposé en caisse. Les retours déjà faits gardent ce motif.`}
                    confirmLabel="Supprimer"
                    cancelLabel="Annuler"
                    danger
                    onCancel={() => setToDelete(null)}
                    onConfirm={() => {
                        deleteMutation.mutate(toDelete.id);
                        setToDelete(null);
                    }}
                />
            )}
        </Section>
    );
};

// Centred like the catalogue product form
const ReturnsSettingsTab = ({canManage}) => (
    <div className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 space-y-6 sm:space-y-8 w-full">
        <RulesSection canManage={canManage}/>
        <ReasonsSection canManage={canManage}/>
    </div>
);

export default ReturnsSettingsTab;
